import React from 'react';
import { X, FolderOpen, Mic, Database } from 'lucide-react';

const TranscriberProjectDetailModal = ({ 
  project, 
  isEditingProject, 
  editedProjectName, 
  editedTranscripts, 
  projectNameError, 
  onClose, 
  onStartEditing, 
  onSaveEdits, 
  onCancelEditing, 
  onProjectNameChange, 
  onTranscriptChange 
}) => {
  return (
    <div className="flex flex-col h-full max-h-[95vh]">
      {/* Modal Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-neutral-800 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-sky-100 dark:bg-sky-900 rounded-lg flex items-center justify-center">
            <FolderOpen className="w-6 h-6 text-sky-600 dark:text-sky-400" />
          </div>
          <div className="flex-1">
            {isEditingProject ? (
              <div>
                <input
                  type="text"
                  value={editedProjectName}
                  onChange={(e) => onProjectNameChange(e.target.value)}
                  className={`text-xl font-semibold text-gray-900 dark:text-neutral-100 bg-gray-50 dark:bg-neutral-900 border rounded px-3 py-1 focus:outline-none focus:ring-2 focus:border-transparent ${
                    projectNameError 
                      ? 'border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 dark:border-neutral-700 focus:ring-sky-500'
                  }`}
                  placeholder="Enter project name"
                />
                {projectNameError && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{projectNameError}</p>
                )}
              </div>
            ) : (
              <h2 className="text-xl font-semibold text-gray-900 dark:text-neutral-100">
                {project.title || project.name || 'No title'}
              </h2>
            )}
            {isEditingProject && (
              <p className="text-sm text-gray-500 dark:text-neutral-400">
                Edit Mode - Click Save to confirm changes
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isEditingProject ? (
            <>
              <button
                onClick={onSaveEdits}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save
              </button>
              <button
                onClick={onCancelEditing}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={onStartEditing}
              className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors ml-2">
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>
      
      {/* Modal Content */}
      <div className="p-6 overflow-y-auto flex-1">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-neutral-100">File & Transcript</h3>
          {project.files && project.files.length > 0 ? (
            <div className="border border-gray-200 dark:border-neutral-800 rounded-lg p-6">
              {(() => {
                const file = project.files[0];
                return (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 pb-4 border-b border-gray-200 dark:border-neutral-800">
                      <div className="w-10 h-10 bg-sky-100 dark:bg-sky-900 rounded-lg flex items-center justify-center">
                        <Mic className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-neutral-100 text-lg">{file.file_name}</div>
                        <div className="text-sm text-gray-500 dark:text-neutral-400">Audio File</div>
                      </div>
                      {file.file_url && (
                        <div className="ml-4">
                          <audio controls className="w-64 h-8" preload="metadata">
                            <source src={file.file_url} type="audio/mpeg" />
                            <source src={file.file_url} type="audio/wav" />
                            <source src={file.file_url} type="audio/mp4" />
                          </audio>
                        </div>
                      )}
                    </div>
                    
                    {file.transcripts && file.transcripts.length > 0 ? (
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 dark:text-neutral-100 mb-3">Transcript</h4>
                        <div className="bg-gray-50 dark:bg-neutral-900 rounded-lg p-4">
                          {isEditingProject ? (
                            <textarea
                              value={editedTranscripts[file.id] || file.transcripts[0].transcript_text || ''}
                              onChange={(e) => onTranscriptChange(file.id, e.target.value)}
                              className="w-full text-sm text-gray-800 dark:text-neutral-200 leading-relaxed bg-white dark:bg-neutral-950 border border-gray-300 dark:border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-y min-h-[120px]"
                              placeholder="Enter transcript text..."
                            />
                          ) : (
                            <div className="text-sm text-gray-800 dark:text-neutral-200 leading-relaxed whitespace-pre-wrap">
                              {file.transcripts[0].transcript_text || 'No transcript text available'}
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-3 text-center">
                            <div className="text-lg font-semibold text-gray-900 dark:text-neutral-100">
                              {file.transcripts[0].confidence ? `${(file.transcripts[0].confidence * 100).toFixed(1)}%` : 'N/A'}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-neutral-400">Confidence</div>
                          </div>
                          <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-3 text-center">
                            <div className="text-lg font-semibold text-gray-900 dark:text-neutral-100">
                              {file.transcripts[0].language_code || 'Unknown'}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-neutral-400">Language</div>
                          </div>
                          <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-3 text-center">
                            <div className="text-lg font-semibold text-gray-900 dark:text-neutral-100">
                              {file.transcripts[0].word_count || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-neutral-400">Word Count</div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-neutral-400">
                        <Mic className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                        <p>No transcript available for this file</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-neutral-400">
              <Database className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p>No file found in this project</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranscriberProjectDetailModal;

